import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { newServer } from '../../constants/constants';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

interface Category {
  id: number;
  users_id: number;
  lawyer_skill_id: number;
  date_created: string;
  category_name: string;
}

interface SeoSettings {
  title?: string;
  description?: string;
  keywords?: string;
}

interface Language {
  id: number;
  user_id: string;
  languages_id: string;
  date_created: string;
  submerchant_id: number;
  lenguaje_name: string;
}

interface Lawyer {
  uuid: string;
  first_name: string;
  last_name: string;
  specialties?: string[];
  languajes?: Language[];
  experience?: string;
  default_amount?: string;
  avatar?: string;
  rating?: number;
  reviews?: number;
  location?: string;
  link_appoinment?: string;
  categories?: Category[];
  lenguajes?: Language[];
  logo?: string;
  description_info?: string;
}

// Función que simula la generación de keywords (puedes integrarla con una API AI real)
const getSeoKeywords = (locale: string): string => {
  if (locale === 'es') {
    return 'Consulta Inicial, Abogados, México, Asesoría Legal, Consultoría Legal';
  } else {
    return 'Initial Consultation, Attorneys, Texas, Legal Advice, Law Firm';
  }
};

const LawyerProfile: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [lawyer, setLawyer] = useState<Lawyer>({} as Lawyer);
  const [seoSettings, setSeoSettings] = useState<SeoSettings>({});
  const locale = navigator.language.startsWith('es') ? 'es' : 'en';
  const seoKeywords = getSeoKeywords(locale);

  useEffect(() => {
    if (uuid) {
      fetch(`${newServer}/directory/lawyer/${uuid}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Error fetching lawyer data: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setLawyer(data.user[0]);
        })
        .catch((err) => {
          console.error('Error fetching lawyer data:', err);
        });

      fetch(`${newServer}/seo-settings/lawyer-profiles-users/${uuid}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Error fetching SEO settings: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setSeoSettings(data);
        })
        .catch((err) => {
          console.error('Error fetching SEO settings:', err);
        });
    }
  }, [uuid]);

  const getSettings = async () => {
    const response = await fetch(`${newServer}seo-settings/lawyer-profiles-users/${uuid}`, {
      method: 'GET',
    });

    return await response.json();
  };

  useEffect(() => {
    const updateMetaTags = async () => {
      try {
        const settings = await getSettings();
        setSeoSettings(settings);
      } catch (error) {
        console.error('Error updating meta tags:', error);
      }
    };

    updateMetaTags();
  }, []);

  return (
    <>
      <Helmet>
        <title>{seoSettings.title}</title>
        <meta name='description' content={seoSettings.description} />
        <meta name='keywords' content={seoSettings.keywords} />
        <link
          rel='alternate'
          hrefLang={locale.startsWith('es') ? 'es-co' : 'en-us'}
          href={window.location.origin + window.location.pathname}
        />
      </Helmet>
      <div
        className='min-h-screen bg-background p-3 sm:p-0'
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        <div className='max-w-4xl sm:max-w-7xl mb:max-w-7wl mx-auto bg-white shadow-lg rounded-lg p-5 sm:p-4'>
          <div className='flex sm:flex-col md:flex-col items-center mb-8'>
            <img
              src={`${lawyer.logo ? lawyer.logo : '/avatars/profile.jpg'}`}
              alt={`${lawyer.first_name} ${lawyer.last_name}`}
              className='w-48 h-48 rounded-full object-contain bg-black mr-8 sm:mr-0 md:mr-0'
            />
            <div>
              <h1
                className='text-4xl font-bold text-green-700 capitalize mb-2'
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {lawyer.first_name} {lawyer.last_name}
              </h1>
              
              <p className='text-foreground-muted mb-2' style={{ fontFamily: 'Poppins, sans-serif' }}>
                Immigration Lawyer
              </p>
              
              {lawyer.lenguajes && lawyer.lenguajes.length > 0 && (
                <p className='text-foreground-subtle'>
                  Languages:{' '}
                  {lawyer.lenguajes.map((languaje, index) => (
                    <span key={languaje.id}>
                      {languaje.lenguaje_name}
                      {index < (lawyer.lenguajes?.length ?? 0) - 1 && ', '}
                    </span>
                  ))}
                </p>
              )}
            </div>
          </div>

          <div className='mb-8'>
            <h2 className='text-2xl  text-foreground mb-4'>About</h2>
            <p className='text-foreground' style={{ fontFamily: 'Poppins, sans-serif' }}>
              {' '}
              {
                `${lawyer.description_info}`?`${lawyer.description_info}`: `is a dedicated immigration lawyer with extensive experience in helping individuals
                navigate complex immigration challenges. With a passion for client success and a deep
                understanding of immigration law, he provides personalized legal strategies tailored
                to each client's unique situation.`
              }
            </p>
            {/* TODO Configurable Variable from Submerchant PML Backoffice Profile / Directory Settings */}
          </div>

          {lawyer.categories && lawyer.categories.length > 0 && (
            <div className='mb-8'>
              <h2 className='text-2xl font-semibold text-foreground mb-4'>Practice Areas</h2>
              {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              <div className='flex flex-wrap gap-2'>
                {lawyer.categories.map((specialty: any) => (
                  <span
                    key={specialty.id}
                    className='bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded'
                  >
                    {specialty.category_name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className='mb-8' style={{ fontFamily: 'Poppins, sans-serif' }}>
            {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Page Font Settings  */}
            <h2
              className='text-2xl font-semibold text-foreground mb-4'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Consultation Details
            </h2>
            {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
            <div className='bg-blue-50 p-4 rounded-lg'>
              <p className='text-foreground mb-2'>
                <strong style={{ fontFamily: 'Poppins, sans-serif' }}>Consultation Type:</strong>
                {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              </p>
              {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Page Font Settings  */}
              <p className='text-foreground mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
                {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Page Font Settings  */}
                <strong>Rate:</strong>{' '}
                {Number(lawyer.default_amount?.replace('$', '') || '0') > 0
                  ? `Starting at ${lawyer.default_amount} per Appointment`
                  : `Free Appointment`}
                {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              </p>
              <div className='flex items-center flex-col'>
                <Button
                  variant='contained'
                  color='primary'
                  className='mr-4 mb-3'
                  onClick={() => (window.location.href = lawyer.link_appoinment || '/')}
                >
                  Book Consultation
                </Button>
                {/* TODO Configurable Button Title Variable from Super Admin Lawyer Profile Theme Settings  */}
                <p
                  className='text-foreground-muted text-sm mt-2'
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  *Please read consultation guidelines before booking
                </p>
                {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              </div>
            </div>
          </div>

          <div>
            <h2 className='text-2xl font-semibold text-foreground mb-4'>Consultation Guidelines</h2>
            {/* TODO Configurable SECTION from Super Admin Lawyer Profile Theme Settings  */}
            <ul className='list-disc list-inside text-foreground space-y-2'>
              <li>Do not call the office regarding consultations</li>
              {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              <li>
                The office only answers calls from existing clients, courts, and government agencies
              </li>
              {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              <li>All questions must be asked via the paymylawyer platform</li>
              {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
              <li>Follow-up questions will require a new consultation appointment</li>
              {/* TODO Configurable Variable from Super Admin Lawyer Profile Theme Settings  */}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default LawyerProfile;
